import { type ExperimentalNoIndexDiff, Replicache } from "@rocicorp/replicache"
import type { SessionDetail } from "@teamhanko/hanko-elements"
import { batch, createSignal, onCleanup } from "solid-js"
import { isValid } from "@/context/hanko"
import { mutators } from "@/data/mutators"
import { db } from "./database"
import { sync } from "./search-sync"

const BACKEND_URL = import.meta.env.VITE_FIKA_BACKEND_URL

export function initReplicache(session: SessionDetail) {
  const [loaded, setLoaded] = createSignal(false)
  const sessionValid = isValid(session)
  const pushURL = sessionValid
    ? `${BACKEND_URL}/api/replicache/push`
    : undefined
  const auth = `Bearer ${session.jwt}`
  const licenseKey = import.meta.env.VITE_REPLICACHE_KEY
  const logLevel = import.meta.env.VITE_ENV === "development" ? "debug" : "info"

  function pullURL() {
    return sessionValid ? `${BACKEND_URL}/api/replicache/pull` : undefined
  }

  const replicache = new Replicache({
    name: `light-${session.userID}`,
    licenseKey,
    pullURL: pullURL(),
    pushURL,
    auth,
    mutators,
    logLevel,
  })

  replicache.experimentalWatch(
    diffs => {
      batch(() => {
        loadDiffs(diffs)
        if (!loaded()) setLoaded(true)
      })
      sync(diffs)
    },
    { initialValuesInFirstDiff: true },
  )

  onCleanup(() => {
    replicache.close()
  })

  return { replicache, db, loaded }
}

// Load all the data to memory. At some point we might want to
// use a lazy approach, but this is good enough for now.
function loadDiffs(diffs: ExperimentalNoIndexDiff) {
  for (const diff of diffs) {
    const [namespace, id] = diff.key.split("/")

    const table = db[namespace as keyof typeof db]
    const op = diff.op
    let value = null
    if (!table) continue

    if (op === "del") {
      table.del(id!)
    } else {
      value = diff.newValue as any
      table.upsert(id!, value)
    }
  }
}
